/**
 * Chat V2 — local enum definitions.
 *
 * Wire VALUES are the strings the backend's handlers expect and must not
 * change. Enum NAMES and member names are FE-facing and chosen for readability,
 * so the two are free to differ.
 */

/**
 * Knowledge source / chat type. Drives both the `knowledge_type` and
 * `chat_type` wire fields. The member names describe the *kind* of knowledge
 * corpus rather than a product name, so a rebrand never reaches the code.
 */
export enum KNOWLEDGE_TYPE {
  WORLD = 'C',
  ENTERPRISE = 'E',
  ASSISTANT = 'A'
}

/**
 * Chat mode discriminator — wire field `chat_class`. The single-letter codes
 * are what the BE expects on every send-message request.
 */
export enum CHAT_CLASS {
  ASSISTANT = 'A',
  NORMAL = 'M',
  SMART_MEET = 'SM',
  DATA_ANALYST = 'I',
  PROJECT = 'P'
}

/**
 * Role of a single message in the conversation — wire field `type` on the
 * message row. Member names spell out the meaning behind the wire letters
 * (`q` = query, `r` = response).
 */
export enum MESSAGE_TYPE {
  QUERY = 'q',
  RESPONSE = 'r'
}

/**
 * Lifecycle status of a file attachment chip in the Composer. Failed or
 * rejected files never get a chip, so there is no failure status. Includes the
 * bulk-import-flow state `PENDING`, which is not rendered today.
 */
export enum UPLOAD_STATUS {
  IDLE = 'idle',
  UPLOADING = 'uploading',
  UPLOADED = 'uploaded',
  PROCESSING = 'processing',
  PENDING = 'pending'
}

/**
 * Tool the user can engage from the Composer's Tools menu. FE-facing: the projection to
 * the BE's boolean flags (`web_search_enabled` etc.) lives in `Composer.tsx`.
 */
export enum CHAT_TOOL {
  WEB_SEARCH = 'web-search',
  COMPARE_MODELS = 'compare-models',
  IMAGE_GENERATION = 'image-generation'
}

/**
 * How a tool combines with the others when the user selects it.
 */
export enum TOOL_SELECTION {
  ADDITIVE = 'additive',
  EXCLUSIVE = 'exclusive'
}

/**
 * User reaction on an assistant response. FE display state; the `messageMappers` layer
 * translates it to the wire's `reaction` / `reaction_code` pair when sending.
 */
export enum REACTION {
  LIKE = 'like',
  DISLIKE = 'dislike'
}

/**
 * Image-generation model the user picks from the Tools → Image generation submenu.
 * Rides the send payload as `image_generation_tool`; the values are the BE's vocabulary
 * verbatim (NANO_BANANA = `'nano-banana'`).
 */
export enum IMAGE_GEN_TOOL {
  NANO_BANANA = 'nano-banana'
}

/**
 * Chat event type discriminator — the `event` field on every PubSub frame.
 * Values must match the BE's published strings verbatim.
 */
export enum CHAT_EVENT {
  STREAM = 'stream',
  CHAT_META = 'chat_meta',
  CHAT_STEP = 'chat_step',
  ERROR = 'error'
}

/**
 * Transport-level frame types — envelopes the socket layer consumes itself, before any
 * chat or step domain sees a frame. `CHUNKED_MESSAGE` is one slice of a frame the
 * backend split to fit Web PubSub's 1 MB message limit; see
 * `transport/frameReassembly.ts`.
 */
export enum TRANSPORT_EVENT {
  CHUNKED_MESSAGE = 'chunked_message'
}

/**
 * Possible values of the `step_name` field on `chat_meta` and `chat_step` frames. The
 * `FINAL` member is the critical one: it marks the end of a streaming turn (the BE
 * emits `chat_meta` more than once; only the `FINAL` frame closes the stream).
 */
export enum CHAT_STEP_NAME {
  TOOL_INPUT = 'tool_input',
  DOCUMENT_RECEIVED = 'document_received',
  PLAN = 'plan',
  ROUTE_SELECTED = 'route_selected',
  IMAGE_GENERATION = 'image_generation',
  TOOL_END = 'tool_end',
  FINAL = 'final',
  INTERRUPT = 'interrupt',
  TOOL_SELECTED = 'tool_selected'
}

/**
 * Groups inside the composer's `+` menu, in render order. The menu draws ONE separator
 * between them, so a group is the only thing an entry has to declare to sit in the
 * right half of the menu.
 */
export enum PLUS_MENU_GROUP {
  SOURCES = 'sources',
  CAPABILITIES = 'capabilities'
}

/** SOURCES-group `+` menu rows a chat mode can allow via `composer.sources`. */
export enum PLUS_MENU_SOURCE {
  UPLOAD_FILE = 'upload-file',
  SELECT_FILE = 'select-file',
  CONNECTORS = 'connectors',
  SKILLS = 'skills'
}

/**
 * One-shot commands a `+` menu row can fire. Kept as an enum rather than a callback on
 * the descriptor so the registry stays serialisable DATA and every handler lives at one
 * interpretation site (`PlusMenu`) — see `plusMenu/plusMenuRegistry.ts`.
 */
export enum PLUS_MENU_ACTION {
  UPLOAD_FILE = 'upload-file'
}

/**
 * Nested panels a `+` menu row can open. Each id maps to exactly one component
 * in `plusMenu/submenuRegistry.tsx`, the same id→component idiom as
 * `interruptViewRegistry` and `stepDetailRegistry`.
 */
export enum PLUS_MENU_SUBMENU {
  SELECT_FILE = 'select-file',
  CONNECTORS = 'connectors',
  // SKILLS = 'skills',
  IMAGE_GENERATION = 'image-generation'
}

export enum TRIGGER_KIND {
  SKILL = 'skill'
}
